import {
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    OutlinedInput,
    Box,
    Avatar
} from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import MainCard from 'ui-component/cards/MainCard';
import CircularLoader from 'ui-component/CircularLoader';
import Transitions from 'ui-component/extended/Transitions';
import { useTheme, styled } from '@mui/material/styles';
import { shouldForwardProp } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import menuItems from 'menu-items';
import PropTypes from 'prop-types';
import { bindToggle } from 'material-ui-popup-state';

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 434,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important'
    },
    [theme.breakpoints.down('lg')]: {
        width: 250
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 4,
        background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff'
    }
}));

const GlobalSearch = ({ viewPort = '', popupState }) => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const [value, setValue] = useState('');
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const prevOpen = useRef(open);
    const [isSearching, setIsSearching] = useState(false);
    const [filterMenus, setFilterMenus] = useState([]);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleSearch = (value) => {
        try {
            setIsSearching(true);
            setValue(value);
            setOpen(value !== '' ? true : false);

            let filterMenus = [];
            for (let menu of menuItems?.items || []) {
                // filter in menu title
                if (dynamicPattern(value).test(menu.title)) {
                    filterMenus.push(menu);
                } else {
                    for (let tag of menu.tags) {
                        // if has sub menus filter in subMenus
                        if (menu?.subMenus && menu?.subMenus?.length > 0) {
                            filterMenus = [...filterMenus, ...processSubMenuTags(menu, value)];
                        }

                        // filter in tags
                        if (dynamicPattern(value).test(tag)) {
                            filterMenus.push(menu);
                            break;
                        }
                    }
                }
            }

            // filtered only id by once
            setFilterMenus(
                Object.values(
                    filterMenus.reduce((acc, item) => {
                        acc[item?.id] = item;
                        return acc;
                    }, {})
                )
            );
        } catch (e) {
            console.log('e', e);
        } finally {
            setIsSearching(false);
        }
    };

    const processSubMenuTags = (menu, value) => {
        const { subMenus } = menu;
        let filterMenus = [];
        for (let subMenu of subMenus) {
            for (let tag of subMenu.tags) {
                // filter in tags
                if (dynamicPattern(value).test(tag)) {
                    filterMenus.push({
                        ...subMenu,
                        mainMenu: menu
                    });
                    break;
                }
            }
        }
        return filterMenus;
    };

    const dynamicPattern = (value) => {
        return new RegExp(value, 'i');
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleNavigate = (menu) => {
        let url = '';
        if (menu?.mainMenu) {
            url = menu?.mainMenu?.url + (menu?.mainMenu ? `?page=${menu?.urlParams}` : '');
        } else {
            url = menu?.url || '';
        }
        window.location.href = url;
    };
    return (
        <>
            {viewPort === 'web' ? (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            ) : (
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position="end">
                            <Box sx={{ ml: 2 }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        ...theme.typography.commonAvatar,
                                        ...theme.typography.mediumAvatar,
                                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.orange.light,
                                        color: theme.palette.orange.dark,
                                        '&:hover': {
                                            background: theme.palette.orange.dark,
                                            color: theme.palette.orange.light
                                        }
                                    }}
                                    {...bindToggle(popupState)}
                                >
                                    <IconX stroke={1.5} size="20px" />
                                </Avatar>
                            </Box>
                        </InputAdornment>
                    }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                    ref={anchorRef}
                />
            )}
            <Popper
                placement="bottom-start"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 5 : 0, 10]
                        }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper sx={{ width: anchorRef.current.offsetWidth }}>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column">
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                                                    <Grid item>
                                                        <Stack direction="row" spacing={2}>
                                                            <Typography variant="subtitle1">Search Results</Typography>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} p={0}>
                                                <Divider sx={{ my: 0 }} />
                                            </Grid>
                                            <Grid item xs={12} sx={{ py: 2 }}>
                                                <Grid container spacing={1} sx={{ px: 2 }}>
                                                    {isSearching ? (
                                                        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                            <CircularLoader size={24} minHeight="0rem" />
                                                        </Grid>
                                                    ) : filterMenus.length === 0 ? (
                                                        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                                            No results
                                                        </Grid>
                                                    ) : (
                                                        <>
                                                            {filterMenus.map((menu, i) => (
                                                                <Grid item key={i}>
                                                                    <Tooltip
                                                                        placement="bottom"
                                                                        title={`Click to go to ${menu?.title || ''}`}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                cursor: 'pointer',
                                                                                color: '#2da8de',
                                                                                textDecoration: 'underline'
                                                                            }}
                                                                        >
                                                                            {menu?.mainMenu && (
                                                                                <Typography
                                                                                    variant="body"
                                                                                    onClick={() => handleNavigate(menu)}
                                                                                >
                                                                                    {menu?.mainMenu?.title} {'> '}
                                                                                </Typography>
                                                                            )}
                                                                            <Typography
                                                                                variant="body"
                                                                                sx={{
                                                                                    fontWeight: 'bold'
                                                                                }}
                                                                                onClick={() => handleNavigate(menu)}
                                                                            >
                                                                                {menu?.title || ''}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Tooltip>
                                                                </Grid>
                                                            ))}
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

GlobalSearch.propTypes = {
    viewPort: PropTypes.string,
    popupState: PropTypes.object
};

export default GlobalSearch;
